// RevenueCat SDK helper for Balagha — native-only (Capacitor iOS + Android).
// On web (www.balagha.tech) all methods no-op safely so the same bundle ships everywhere.
//
// Environment variables (CRA prefix):
//   REACT_APP_REVENUECAT_IOS_KEY      → public app-specific key (starts with "appl_")
//   REACT_APP_REVENUECAT_ANDROID_KEY  → public app-specific key (starts with "goog_")
//
// RevenueCat auto-detects sandbox vs production based on the receipt/purchase source;
// no manual environment switch is required.

import { Capacitor } from "@capacitor/core";

// Dynamic import so the RevenueCat native bindings aren't evaluated on web.
let Purchases = null;
let LOG_LEVEL = null;

async function loadNativeSdk() {
  if (Purchases) return;
  const mod = await import("@revenuecat/purchases-capacitor");
  Purchases = mod.Purchases;
  LOG_LEVEL = mod.LOG_LEVEL;
}

let initialized = false;
let currentUserId = null;
let listenerId = null;

function getApiKey() {
  const platform = Capacitor.getPlatform();
  if (platform === "ios") return process.env.REACT_APP_REVENUECAT_IOS_KEY || "";
  if (platform === "android") return process.env.REACT_APP_REVENUECAT_ANDROID_KEY || "";
  return "";
}

/** Configure the SDK. Safe to call multiple times. */
export async function init(userId = null) {
  if (!Capacitor.isNativePlatform()) return false;
  await loadNativeSdk();

  const apiKey = getApiKey();
  if (!apiKey) {
    // Fail soft — production build wouldn't ship without a key; dev builds shouldn't crash.
    console.warn("[RevenueCat] Missing SDK key for", Capacitor.getPlatform());
    return false;
  }

  await Purchases.setLogLevel({ level: LOG_LEVEL.INFO });

  if (!initialized) {
    await Purchases.configure({
      apiKey,
      ...(userId ? { appUserID: userId } : {}),
    });
    initialized = true;
    currentUserId = userId;
    return true;
  }

  // Anonymous → authenticated migration (preserves any prior sandbox purchases).
  if (userId && userId !== currentUserId) {
    await Purchases.logIn({ appUserID: userId });
    currentUserId = userId;
  }
  return true;
}

/** Returns the current offering's available packages (array). Empty on web. */
export async function getOfferings() {
  if (!Capacitor.isNativePlatform()) return [];
  await loadNativeSdk();
  const result = await Purchases.getOfferings();
  const offerings = result.offerings ?? result;
  return offerings.current?.availablePackages ?? [];
}

/** Purchase a package; returns the updated CustomerInfo. */
export async function purchasePackage(pkg) {
  if (!Capacitor.isNativePlatform()) {
    throw new Error("Purchases are only available in the native app.");
  }
  await loadNativeSdk();
  const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
  return customerInfo;
}

/** Restore previous purchases; returns CustomerInfo. */
export async function restore() {
  if (!Capacitor.isNativePlatform()) return null;
  await loadNativeSdk();
  const { customerInfo } = await Purchases.restorePurchases();
  return customerInfo;
}

/** Snapshot the current CustomerInfo. */
export async function getCustomerInfo() {
  if (!Capacitor.isNativePlatform()) return null;
  await loadNativeSdk();
  const { customerInfo } = await Purchases.getCustomerInfo();
  return customerInfo;
}

/** Subscribe to real-time CustomerInfo updates (renewals, refunds, revocations). */
export async function onCustomerInfoUpdate(cb) {
  if (!Capacitor.isNativePlatform()) return null;
  await loadNativeSdk();
  listenerId = await Purchases.addCustomerInfoUpdateListener(({ customerInfo }) =>
    cb(customerInfo)
  );
  return listenerId;
}

/** Unsubscribe from CustomerInfo updates. */
export async function offCustomerInfoUpdate() {
  if (!listenerId || !Capacitor.isNativePlatform()) return;
  await loadNativeSdk();
  await Purchases.removeCustomerInfoUpdateListener(listenerId);
  listenerId = null;
}

/** Convenience: derive entitlement id list from CustomerInfo. */
export function activeEntitlements(customerInfo) {
  if (!customerInfo) return [];
  const ents = customerInfo.entitlements?.active || {};
  return Object.keys(ents);
}
